/* eslint-disable no-prototype-builtins */
import Form from 'antd/lib/form';
import Space from 'antd/lib/space';
import Button from 'antd/lib/button';
import { ArrowRightOutlined } from '@ant-design/icons';
import { isEqual } from 'lodash';
import * as React from 'react';
import { FormFieldData, renderFormFields } from '../../../../helpers/formHelpers';
import { SetupViewProps } from '../../../../typescriptHelpers';
import { Integration, IntegrationType, WorkflowType } from '../../../../types';
import {
    tryUpdateIntegrationRequest,
    UpdateIntegrationRequest,
    UpdateIntegrationRequestDataType,
} from '../../../../actions/integrations';
import { useDispatch, useSelector } from 'react-redux';
import { getIsLoading } from '../../../../helpers';
import {
    integrationUpdatableForIntegrationType,
    parseIsPipelineFromFormValues,
} from '../../../../helpers/integrationHelpers';
import { RootState } from '../../../../reducers/reducers';
import { IntegrationUpdatable } from '../../../../actions/updatable';
import { FormInstance } from 'antd/lib/form';

export interface WorkflowModuleProps extends SetupViewProps {
    form: FormInstance<any>;
    fields: FormFieldData[];
    integration: Integration;
    integrationSetupType: IntegrationUpdatable;
}

export const buildPartialUpdateRequest: (
    integrationId: IntegrationType,

    values: any,
) => Partial<UpdateIntegrationRequestDataType> = (integrationId, values) => {
    const data: Partial<UpdateIntegrationRequestDataType> = {};
    switch (integrationId) {
        case IntegrationType.Codemagic: {
            if (values.hasOwnProperty('slugAndWorkflowId')) {
                const workflowId = values.slugAndWorkflowId?.[1] ?? '';
                data['workflowId'] = workflowId;
                const appId = values.slugAndWorkflowId?.[0] ?? '';
                data['appSlug'] = appId;
            }

            if (values.hasOwnProperty('slugAndDevWorkflowId')) {
                const devWorkflowId = values.slugAndDevWorkflowId?.[1] ?? '';
                data['devWorkflowId'] = devWorkflowId;
                const devAppId = values.slugAndDevWorkflowId?.[0] ?? '';
                data['devAppSlug'] = devAppId;
            }

            if (values.hasOwnProperty('slugAndProdWorkflowId')) {
                const prodWorkflowId = values.slugAndProdWorkflowId?.[1] ?? '';
                data['prodWorkflowId'] = prodWorkflowId;
                const prodAppId = values.slugAndProdWorkflowId?.[0] ?? '';
                data['prodAppSlug'] = prodAppId;
            }

            if (values.hasOwnProperty('allowMatchingMultipleAppStoreBuilds')) {
                data['allowMatchingMultipleAppStoreBuilds'] = values.allowMatchingMultipleAppStoreBuilds ?? false;
            }

            break;
        }
        case IntegrationType.BitbucketCI: {
            if (values.hasOwnProperty('workflowId')) {
                const workflowId = values.workflowId ?? '';
                data['workflowId'] = workflowId;
            }

            if (values.hasOwnProperty('devWorkflowId')) {
                const devWorkflowId = values.devWorkflowId ?? '';
                data['devWorkflowId'] = devWorkflowId;
            }

            if (values.hasOwnProperty('prodWorkflowId')) {
                const prodWorkflowId = values.prodWorkflowId ?? '';
                data['prodWorkflowId'] = prodWorkflowId;
            }
            break;
        }
        case IntegrationType.Bitrise: {
            const isPipeline = parseIsPipelineFromFormValues(values);
            data['isPipeline'] = isPipeline;

            if (values.hasOwnProperty('slugAndWorkflowId') && isPipeline[WorkflowType.Staging] != true) {
                const workflowId = values.slugAndWorkflowId?.[1] ?? '';
                data['workflowId'] = workflowId;
                const appSlug = values.slugAndWorkflowId?.[0] ?? '';
                data['appSlug'] = appSlug;
            } else if (
                values.hasOwnProperty('workflowId') &&
                values.hasOwnProperty('appSlug') &&
                isPipeline[WorkflowType.Staging] == true
            ) {
                const workflowId = values.workflowId;
                data['workflowId'] = workflowId;
                const appSlug = values.appSlug;
                data['appSlug'] = appSlug;
            }

            if (values.hasOwnProperty('slugAndDevWorkflowId') && isPipeline[WorkflowType.Working] != true) {
                const devWorkflowId = values.slugAndDevWorkflowId?.[1] ?? '';
                data['devWorkflowId'] = devWorkflowId;
                const devAppSlug = values.slugAndDevWorkflowId?.[0] ?? '';
                data['devAppSlug'] = devAppSlug;
            } else if (
                values.hasOwnProperty('workflowIdDev') &&
                values.hasOwnProperty('appSlugDev') &&
                isPipeline[WorkflowType.Working] == true
            ) {
                const devWorkflowId = values.workflowIdDev;
                data['devWorkflowId'] = devWorkflowId;
                const devAppSlug = values.appSlugDev;
                data['devAppSlug'] = devAppSlug;
            }

            if (values.hasOwnProperty('slugAndProdWorkflowId') && isPipeline[WorkflowType.Production] != true) {
                const prodWorkflowId = values.slugAndProdWorkflowId?.[1] ?? '';
                data['prodWorkflowId'] = prodWorkflowId;
                const prodAppSlug = values.slugAndProdWorkflowId?.[0] ?? '';
                data['prodAppSlug'] = prodAppSlug;
            } else if (
                values.hasOwnProperty('workflowIdProd') &&
                values.hasOwnProperty('appSlugProd') &&
                isPipeline[WorkflowType.Production] == true
            ) {
                const prodWorkflowId = values.workflowIdProd;
                data['prodWorkflowId'] = prodWorkflowId;
                const prodAppSlug = values.appSlugProd;
                data['prodAppSlug'] = prodAppSlug;
            }

            if (values.hasOwnProperty('allowMatchingMultipleAppStoreBuilds')) {
                data['allowMatchingMultipleAppStoreBuilds'] = values.allowMatchingMultipleAppStoreBuilds ?? false;
            }

            break;
        }
        case IntegrationType.Buildkite: {
            data['orgId'] =
                values.orgIdAndWorkflowId?.[0] ??
                values.orgIdAndProdWorkflowId?.[0] ??
                values.orgIdAndDevWorkflowId?.[0];

            if (values.hasOwnProperty('orgIdAndWorkflowId')) {
                const workflowId = values.orgIdAndWorkflowId?.[1] ?? '';
                data['workflowId'] = workflowId;
            }

            if (values.hasOwnProperty('orgIdAndDevWorkflowId')) {
                const devWorkflowId = values.orgIdAndDevWorkflowId?.[1] ?? '';
                data['devWorkflowId'] = devWorkflowId;
            }

            if (values.hasOwnProperty('orgIdAndProdWorkflowId')) {
                const prodWorkflowId = values.orgIdAndProdWorkflowId?.[1] ?? '';
                data['prodWorkflowId'] = prodWorkflowId;
            }
            break;
        }
        case IntegrationType.CircleCI: {
            if (values.hasOwnProperty('repoName')) {
                const repoName = values.repoName ?? '';
                data['repoName'] = repoName;
            }

            if (values.hasOwnProperty('collaborationId')) {
                const collaborationId = values.collaborationId ?? '';
                data['collaborationId'] = collaborationId;
            }

            if (values.hasOwnProperty('workflowId')) {
                const workflowId = values.workflowId ?? '';
                data['workflowId'] = workflowId;
            }

            if (values.hasOwnProperty('devWorkflowId')) {
                const devWorkflowId = values.devWorkflowId ?? '';
                data['devWorkflowId'] = devWorkflowId;
            }

            if (values.hasOwnProperty('prodWorkflowId')) {
                const prodWorkflowId = values.prodWorkflowId ?? '';
                data['prodWorkflowId'] = prodWorkflowId;
            }
            break;
        }
        case IntegrationType.GitHubCI: {
            data['repoName'] =
                values.repoNameAndWorkflow?.[0] ??
                values.repoNameAndProdWorkflow?.[0] ??
                values.repoNameAndDevWorkflow?.[0];

            if (values.hasOwnProperty('repoNameAndWorkflow')) {
                const workflowId = values.repoNameAndWorkflow?.[1] ?? '';
                data['workflowId'] = workflowId;
            }

            if (values.hasOwnProperty('repoNameAndDevWorkflow')) {
                const devWorkflowId = values.repoNameAndDevWorkflow?.[1] ?? '';
                data['devWorkflowId'] = devWorkflowId;
            }

            if (values.hasOwnProperty('repoNameAndProdWorkflow')) {
                const prodWorkflowId = values.repoNameAndProdWorkflow?.[1] ?? '';
                data['prodWorkflowId'] = prodWorkflowId;
            }
            break;
        }
        case IntegrationType.GitLabCI: {
            if (values.hasOwnProperty('projectId')) {
                const projectId = values.projectId ?? '';
                data['projectId'] = projectId;
            }

            if (values.hasOwnProperty('workflowId')) {
                const workflowId = values.workflowId ?? '';
                data['workflowId'] = workflowId;
            }

            if (values.hasOwnProperty('devWorkflowId')) {
                const devWorkflowId = values.devWorkflowId ?? '';
                data['devWorkflowId'] = devWorkflowId;
            }

            if (values.hasOwnProperty('prodWorkflowId')) {
                const prodWorkflowId = values.prodWorkflowId ?? '';
                data['prodWorkflowId'] = prodWorkflowId;
            }
            break;
        }
        case IntegrationType.Jenkins: {
            if (values.hasOwnProperty('workflowId')) {
                const workflowId = values.workflowId ?? '';
                data['workflowId'] = workflowId;
            }

            if (values.hasOwnProperty('devWorkflowId')) {
                const devWorkflowId = values.devWorkflowId ?? '';
                data['devWorkflowId'] = devWorkflowId;
            }

            if (values.hasOwnProperty('prodWorkflowId')) {
                const prodWorkflowId = values.prodWorkflowId ?? '';
                data['prodWorkflowId'] = prodWorkflowId;
            }

            if (values.hasOwnProperty('subWorkflowId')) {
                const subWorkflowId = values.subWorkflowId ?? '';
                data['subWorkflowId'] = subWorkflowId;
            }

            if (values.hasOwnProperty('devSubWorkflowId')) {
                const devSubWorkflowId = values.devSubWorkflowId ?? '';
                data['devSubWorkflowId'] = devSubWorkflowId;
            }

            if (values.hasOwnProperty('prodSubWorkflowId')) {
                const prodSubWorkflowId = values.prodSubWorkflowId ?? '';
                data['prodSubWorkflowId'] = prodSubWorkflowId;
            }

            if (values.hasOwnProperty('allowMatchingMultipleAppStoreBuilds')) {
                data['allowMatchingMultipleAppStoreBuilds'] = values.allowMatchingMultipleAppStoreBuilds ?? false;
            }
            break;
        }
        case IntegrationType.TravisCI: {
            if (values.hasOwnProperty('repoName')) {
                const repoName = values.repoName ?? '';
                data['repoName'] = repoName;
            }
        }
    }
    return data;
};

export const WorkflowModule: React.FC<WorkflowModuleProps> = ({
    appId,
    orgId,
    onChange,
    form,
    fields,
    integration,
    integrationSetupType,
}) => {
    const dispatch = useDispatch();

    const initialValues = {
        ...fields.reduce(
            (acc, field) => ({
                ...acc,
                [field.name]: field.initialValue,
            }),
            {},
        ),
    };

    const checkIfHasUnsavedChanges = () => {
        const formValues = form.getFieldsValue();
        return !isEqual(formValues, initialValues);
    };

    const updateIntegrationIsLoading = useSelector((state: RootState) =>
        getIsLoading(integrationSetupType, state, integration?.installationId),
    );

    const updateIntegration = (values) => {
        const data = buildPartialUpdateRequest(integration.id, values);
        const type = integrationUpdatableForIntegrationType(integration.id);

        const updateRequest: UpdateIntegrationRequest = {
            orgId,
            appId,
            integrationId: integration.installationId,
            data,
            isPartialUpdate: true,
        };

        dispatch(tryUpdateIntegrationRequest(type, updateRequest));
    };

    return (
        <Form
            form={form}
            onFinish={updateIntegration}
            initialValues={initialValues}
            onFieldsChange={() => {
                onChange(checkIfHasUnsavedChanges());
            }}
        >
            <Space direction="vertical" style={{ width: '100%' }} size={20}>
                {renderFormFields(fields)}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'end',
                    }}
                >
                    <Form.Item style={{ marginBottom: 0 }} shouldUpdate>
                        {() => (
                            <Button
                                loading={updateIntegrationIsLoading}
                                disabled={!checkIfHasUnsavedChanges()}
                                type="primary"
                                htmlType="submit"
                                icon={<ArrowRightOutlined />}
                            >
                                Save
                            </Button>
                        )}
                    </Form.Item>
                </div>
            </Space>
        </Form>
    );
};
